export interface ValidationResult {
  valid: boolean;
  message?: string;
}

/* ------------------------------------------------------------
   REQUIRED VALIDATIONS
------------------------------------------------------------ */

// ✔ Age must be between 18–100
export const validateAge = (value: string | number): ValidationResult => {
  const age = Number(value);
  if (!age || age < 18 || age > 100) {
    return { valid: false, message: "Age must be between 18 and 100." };
  }
  return { valid: true };
};

// ✔ Phone number = exactly 10 digits
export const validatePhone = (value: string): ValidationResult => {
  if (!/^\d{10}$/.test(value)) {
    return { valid: false, message: "Phone number must be exactly 10 digits." };
  }
  return { valid: true };
};

// ✔ Final question — ALL checkboxes must be selected
export const validateAllChecksSelected = (
  selectedValue: string,
  options: string[]
): ValidationResult => {
  const selected = selectedValue
    ? selectedValue.split(",").filter((item) => item.trim() !== "")
    : [];

  if (selected.length !== options.length) {
    return {
      valid: false,
      message: "Please agree to all statements before proceeding.",
    };
  }
  return { valid: true };
};

/* ------------------------------------------------------------
   RECOMMENDED VALIDATIONS
------------------------------------------------------------ */

// ✔ Q1: Name — minimum 2 characters
export const validateName = (value: string): ValidationResult => {
  if (!value || value.trim().length < 2) {
    return { valid: false, message: "Please enter your full name." };
  }
  return { valid: true };
};

// ✔ Q6: Instagram handle — must start with @ and be 4+ chars
export const validateInstagram = (value: string): ValidationResult => {
  if (!/^@[A-Za-z0-9._]{3,}$/.test(value)) {
    return {
      valid: false,
      message: "Instagram handle must start with @ and be 4+ characters.",
    };
  }
  return { valid: true };
};

// ✔ Q8: Age range format — “20-25”
export const validateAgeRange = (value: string): ValidationResult => {
  if (!/^\d{2}-\d{2}$/.test(value)) {
    return {
      valid: false,
      message: "Please enter age range in format: 20-25",
    };
  }
  return { valid: true };
};

// ✔ Q13 & Q14: Minimum 2 characters
export const validateMinText = (
  value: string,
  field: string
): ValidationResult => {
  if (!value || value.trim().length < 2) {
    return { valid: false, message: `Please enter a valid ${field}.` };
  }
  return { valid: true };
};

// ✔ Q20 & Q21: Minimum 10 characters
export const validateTextarea = (
  value: string,
  label: string
): ValidationResult => {
  if (!value || value.trim().length < 10) {
    return {
      valid: false,
      message: `${label} must be at least 10 characters.`,
    };
  }
  return { valid: true };
};

/* ------------------------------------------------------------
   MAIN VALIDATION ROUTER
   (connects question ID → validation function)
------------------------------------------------------------ */

export const validateFieldById = (
  questionId: number,
  value: string,
  questionOptions?: string[]
): ValidationResult => {
  switch (questionId) {
    case 1:
      return validateName(value);

    case 2:
      return validateAge(value);

    case 5:
      return validatePhone(value);

    case 6:
      return validateInstagram(value);

    case 8:
      return validateAgeRange(value);

    case 13:
      return validateMinText(value, "occupation");

    case 14:
      return validateMinText(value, "hobbies or interests");

    case 20:
      return validateTextarea(value, "Green flag");

    case 21:
      return validateTextarea(value, "Red flag");

    case 23:
      return validateAllChecksSelected(value, questionOptions || []);

    default:
      return { valid: true };
  }
};
